// AIPage — marketing page for the AI relay (中转站) product.
//
// Layout follows the PortalPage rhythm so the two pages read as siblings:
//   • HERO            — eyebrow / big title / description / 2 CTAs / terminal
//   • SECTION 01      — stats strip (4 facts, full-bleed under hero)
//   • SECTION 02      — real per-upstream pricing table (filterable by upstream)
//   • SECTION 03      — multi-protocol (OpenAI SDK + Anthropic SDK side-by-side)
//   • SECTION 04      — upstream relay pool (live cards from marketplace)
//   • CTA strip       — Get Key / View Pricing
//   • Footer          — re-uses the marketing footer from market.jsx
//
// All styling re-uses the existing portal palette: `.term-panel`, `.capability-cell`,
// `.sec-num`, `.section-seam`, `.sec-pad-*`, `.reveal-stagger`, `.gs-section-title`,
// `.slash-mark`, `.mint-text`, `.hero-blob`. No new CSS rules — minimum surface area.

// Public docs portal. Both the hero CTA and the footer CTA strip link out
// here — kept as a constant so the URL changes in one place when we migrate
// off Fern.
const DOCS_URL = 'https://xapi-to.docs.buildwithfern.com/';

// 暂时隐藏 /ai 公开页所有真实「调用量(calls / tokens)」统计展示
// （catalog 三视图 + hero 总调用 + 上游卡片 hits）。改回 true 即恢复。
// 注：hero 终端示例里的 token 数字是演示用，不受此开关影响。
const SHOW_CALL_VOLUME = false;

import React, { useState as uS, useEffect as uE, useRef as uR, useMemo as uM } from 'react';
import Balancer from 'react-wrap-balancer';
import { XPrim, XIcons } from '../primitives.jsx';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { catalogRow, formatPrice, loadModelCatalog, MODEL_CATEGORIES } from './model-pricing.js';
import { audioPriceText } from '../components/ModelReferencePrices.jsx';
import { useT } from '../i18n.jsx';
import { Tooltip, TooltipProvider } from '../ui/tooltip.jsx';
import { Footer, ApiAvatar } from './market.jsx';

// ---------- helpers ----------

const useReveal = (deps = []) => {
  const ref = uR(null);
  uE(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') { node?.classList?.add('is-revealed'); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    io.observe(node);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
};

const RevealSection = ({ children, ...props }) => {
  const ref = useReveal();
  return (
    <section ref={ref} className={`reveal ${props.className || ''}`} style={props.style}>
      {children}
    </section>
  );
};

// UpstreamCard —— 真实 marketplace 数据卡片，复用 /market 的视觉
// （彩色 letter avatar、verified、调用量·延迟·成功率、迷你 sparkline）。
// 点击进 detail 页。
const UpstreamCard = ({ r, navigate, t }) => {
  const meta = XPrim.categoryMeta(r.category);
  const uptime = Number(r.uptime || 100);
  const verified = !!r.verified;
  return (
    <div
      className="card"
      onClick={() => navigate('api-detail', { id: XApi.helpers.apiSlug(r) })}
      style={{
        cursor:'pointer', padding: 22,
        display:'flex', flexDirection:'column', gap: 14,
        border:'1px solid var(--line)',
        transition:'border-color .18s ease, transform .18s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line-2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line)';   e.currentTarget.style.transform = 'none'; }}
    >
      <div className="row gap-3" style={{ alignItems:'center' }}>
        <ApiAvatar name={r.name} logoUrl={r.logoUrl} meta={meta} size={38} radius={7} fontSize={16}/>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="row gap-1" style={{ alignItems:'center' }}>
            <span style={{
              fontSize: 15, fontWeight: 600, color:'var(--fg-0)',
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              letterSpacing:'-0.01em',
            }}>{r.name}</span>
            {verified && (
              <XIcons.IconCheck size={12} style={{ color:'var(--c-mint)', flexShrink: 0 }}/>
            )}
          </div>
          <div className="micro-mono" style={{
            marginTop: 3, color: meta.hue != null ? meta.fg : 'var(--fg-3)',
            fontSize: 10, letterSpacing:'0.10em', textTransform:'uppercase',
          }}>
            {XPrim.categoryLabel(t, r.category) || 'AI'}
          </div>
        </div>
      </div>

      {r.description && (
        <p className="clamp-2" style={{
          margin: 0, fontSize: 12.5, color:'var(--fg-3)', lineHeight: 1.55,
          minHeight: 38,
        }}>{r.description}</p>
      )}

      <div className="row between" style={{
        paddingTop: 12, borderTop:'1px solid var(--line)', alignItems:'center',
      }}>
        <div className="row gap-2" style={{
          alignItems:'center', fontFamily:'var(--font-mono)', fontSize: 11, color:'var(--fg-3)',
        }}>
          {SHOW_CALL_VOLUME && (
            <>
              <span>{XPrim.fmt(r.hits || 0)} {t('ai.upstream.callsLabel')}</span>
              <span style={{ color:'var(--line-2)' }}>·</span>
            </>
          )}
          <span>{r.latency || 0}ms</span>
          <span style={{ color:'var(--line-2)' }}>·</span>
          <span style={{ color: uptime >= 99 ? 'var(--c-mint)' : 'var(--fg-3)' }}>
            {uptime.toFixed(uptime === 100 ? 0 : 1)}%
          </span>
        </div>
        {r.spark && r.spark.length > 1 && (
          <span style={{ color:'var(--c-mint)', opacity: 0.7 }}>
            <XPrim.Sparkline values={r.spark} width={70} height={18}/>
          </span>
        )}
      </div>
    </div>
  );
};

// BaseUrlChip —— 鲜明显示所有 AI 上游的统一入口 URL，并提供一键复制
// 放在 hero CTA 下方，与 terminal panel 视觉呼应
const BASE_URL = 'https://ai.xapi.to';
const BaseUrlChip = ({ loaded, t }) => {
  const [copied, setCopied] = uS(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(BASE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className={`reveal delay-3 ${loaded ? 'is-revealed' : ''}`} style={{ marginTop: 22 }}>
      <div
        onClick={copy}
        className="row gap-2"
        style={{
          display:'inline-flex', alignItems:'center', cursor:'pointer',
          padding:'10px 14px', borderRadius: 8,
          background:'var(--bg-1)', border:'1px solid var(--line-2)',
          transition:'border-color .15s, background .15s',
          maxWidth: '100%',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(95,208,168,0.40)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line-2)'; }}
      >
        <span className="micro-mono" style={{
          color:'var(--fg-4)', textTransform:'uppercase', letterSpacing:'0.14em', fontSize: 10,
        }}>
          {t('ai.hero.baseUrl.label')}
        </span>
        <span className="mono" style={{ color:'var(--c-mint)', fontSize: 14, fontWeight: 500 }}>
          {BASE_URL}
        </span>
        <span style={{ color: copied ? 'var(--ok)' : 'var(--fg-4)', flexShrink: 0 }}>
          {copied
            ? <XIcons.IconCheck size={13}/>
            : <XIcons.IconCopy size={13}/>}
        </span>
      </div>
    </div>
  );
};

// Hero terminal — CLI-first quickstart that doubles as the product pitch.
// Our users live in Claude Code / Codex CLI, not in app code — so the tabs show
// how to point each *CLI* at the aggregated entrypoint `ai.xapi.to`, with the
// base_url / BASE_URL line highlighted as "the only line you change".
// Verified: both protocols get auto-route + failover via the same
// ExecutionService, and Codex's default Responses API works out of the box —
// the gateway transparently adapts /v1/responses → /chat/completions
// (libs/ai-specs/src/adaptors/openai_responses.adapter.ts + compose.ts), so no
// wire_api="chat" override is needed. SDK is kept as a 3rd tab for the minority
// who call from code. `code` takes `t` so inline comments localize.
const HERO_SNIPPETS = {
  claudeCode: {
    label: 'Claude Code',
    highlight: 'ANTHROPIC_BASE_URL',
    code: (t) => `${t('ai.hero.term.cc.note')}
export ANTHROPIC_BASE_URL="https://ai.xapi.to"
export ANTHROPIC_API_KEY="sk-xapi-…"
claude`,
    route: { model: 'claude-opus-4-7', upstream: 'skyapi' },
    metrics: { ttft: '184ms', tokens: '312', cost: '$0.0021', official: '$0.0035', save: 40 },
  },
  codex: {
    label: 'Codex CLI',
    highlight: 'base_url =',
    code: (t) => `# ~/.codex/config.toml
model_provider = "xapi"
[model_providers.xapi]
base_url = "https://ai.xapi.to/v1"
env_key  = "XAPI_API_KEY"

${t('ai.hero.term.codex.then')}
export XAPI_API_KEY="sk-xapi-…"
codex`,
    route: { model: 'gpt-5.4', upstream: 'supertoken' },
    metrics: { ttft: '172ms', tokens: '298', cost: '$0.0014', official: '$0.0026', save: 46 },
  },
  sdk: {
    label: 'SDK',
    highlight: 'base_url=',
    code: (t) => `# pip install anthropic
from anthropic import Anthropic

client = Anthropic(
    base_url="https://ai.xapi.to",
    api_key="sk-xapi-…",
)
client.messages.create(
    model="claude-opus-4-7",
    messages=[{"role": "user", "content": "${t('ai.hero.term.userMsg')}"}],
)`,
    route: { model: 'claude-opus-4-7', upstream: 'skyapi' },
    metrics: { ttft: '184ms', tokens: '312', cost: '$0.0021', official: '$0.0035', save: 40 },
  },
};

// 策略后缀打字机 —— base_url 默认是最简入口（无后缀，复制出去即可用）。这里只在
// 渲染时于 ai.xapi.to 之后循环 type /quality → /speed → /cost，暗示「换 URL 后缀
// 换路由策略」（网关支持 /:strategy/v1/...）。纯视觉，不影响复制的干净默认值。
const STRATEGY_SUFFIXES = ['/quality', '/speed', '/cost'];
const useStrategyTypewriter = () => {
  const [s, setS] = uS({ i: 0, len: 0, typing: true });
  uE(() => {
    const full = STRATEGY_SUFFIXES[s.i];
    let delay = s.typing ? 95 : 50;
    if (s.typing && s.len === full.length) delay = 1500;   // 打完停留
    if (!s.typing && s.len === 0) delay = 400;             // 删完停顿
    const id = setTimeout(() => {
      setS((p) => {
        const f = STRATEGY_SUFFIXES[p.i];
        if (p.typing) {
          return p.len < f.length ? { ...p, len: p.len + 1 } : { ...p, typing: false };
        }
        return p.len > 0
          ? { ...p, len: p.len - 1 }
          : { i: (p.i + 1) % STRATEGY_SUFFIXES.length, len: 0, typing: true };
      });
    }, delay);
    return () => clearTimeout(id);
  }, [s]);
  return STRATEGY_SUFFIXES[s.i].slice(0, s.len);
};

// 单行代码/配置上色 —— 不做完整语法高亮，只够 hero 演示用：
//   • base_url / BASE_URL 行（snip.highlight 匹配）：整行 mint 高亮 + 左侧竖条；在
//     ai.xapi.to 后插入打字机策略后缀 + 闪烁光标；行尾「唯一要改的一行」注释
//   • 注释行（# 或 //）：整行染灰
//   • 空行：留出视觉间距
//   • 其它行：把 "..." 双引号字符串染成 term-string
const HeroCodeLine = ({ line, highlight, note, strategySuffix = '' }) => {
  if (line === '') return <div style={{ height: '0.7em' }} />;
  if (highlight && line.includes(highlight)) {
    // 在 ai.xapi.to 后切开，插入打字机后缀（Anthropic 落在末尾、OpenAI 落在 /v1 之前）
    const ANCHOR = 'ai.xapi.to';
    const at = line.indexOf(ANCHOR);
    const before = line.slice(0, at + ANCHOR.length);
    const after = line.slice(at + ANCHOR.length);
    // 在开引号处切：head = 赋值左侧含开引号；url 段 = https://...ai.xapi.to + 打字机后缀 + 收尾。
    // flex-wrap：宽屏 head 与 url 同一行；窄屏 url 整段落到第二行（永不在 URL 内部断词）。
    const q = before.indexOf('"');
    const head = q >= 0 ? before.slice(0, q + 1) : '';
    const urlHead = q >= 0 ? before.slice(q + 1) : before;
    // 隐藏 sizer 用「最长后缀」撑出 url 段固定宽度 → 折行决策与当前后缀无关，逐帧不抖；
    // 真内容绝对定位左对齐，后缀短时多出的空白落在引号右侧（行尾，不可见）→ 无间隙、无滚动条。
    const longestSuffix = STRATEGY_SUFFIXES.reduce((a, b) => (a.length >= b.length ? a : b), '');
    return (
      <div style={{
        background:'rgba(95,208,168,0.10)', borderLeft:'2px solid var(--c-mint)',
        margin:'1px -10px', padding:'1px 10px 1px 8px',
      }}>
        <div style={{ display:'flex', flexWrap:'wrap', alignItems:'baseline' }}>
          {head && <span style={{ color:'var(--c-mint)', whiteSpace:'pre' }}>{head}</span>}
          <span style={{ position:'relative', display:'inline-block', whiteSpace:'pre' }}>
            {/* sizer：撑出最长后缀时的宽度，隐藏占位 */}
            <span aria-hidden style={{ visibility:'hidden' }}>{urlHead}{longestSuffix}{after}</span>
            {/* 真内容：绝对定位左对齐，随打字机伸缩不影响 sizer 宽度 */}
            <span style={{ position:'absolute', left: 0, top: 0, whiteSpace:'pre' }}>
              <span style={{ color:'var(--c-mint)' }}>{urlHead}</span>
              <span style={{ color:'var(--c-mint)', fontWeight: 600 }}>{strategySuffix}</span>
              <span style={{ color:'var(--c-mint)' }}>{after}</span>
              <span className="term-cursor" style={{ verticalAlign:'-2px' }}/>
            </span>
          </span>
        </div>
        {/* 标注独立成行：不参与 URL 换行、窄屏不被撕裂（也即「默认两行」的正确形态） */}
        <div className="micro-mono" style={{
          color:'var(--fg-4)', textTransform:'none', letterSpacing: 0,
          whiteSpace:'pre-wrap', wordBreak:'break-word',
        }}>← {note}</div>
      </div>
    );
  }
  if (/^\s*(#|\/\/)/.test(line)) {
    return (
      <div className="term-comment" style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{line}</div>
    );
  }
  const parts = line.split(/("[^"]*")/g);
  return (
    <div style={{ color:'var(--fg-1)', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
      {parts.map((p, i) =>
        p.startsWith('"') && p.endsWith('"')
          ? <span key={i} className="term-string">{p}</span>
          : <span key={i}>{p}</span>,
      )}
    </div>
  );
};

const HeroTerminal = ({ loaded }) => {
  const { t } = useT();
  const [proto, setProto] = uS('claudeCode');
  const [copied, setCopied] = uS(false);
  const snip = HERO_SNIPPETS[proto];
  const codeText = snip.code(t);
  const strategySuffix = useStrategyTypewriter();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codeText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="term-panel" style={{
      width:'100%', maxWidth: 560, marginLeft:'auto',
      transition:'opacity .8s ease .15s, transform .8s ease .15s',
      opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(14px)',
    }}>
      <div className="term-panel-head row between">
        <div className="row gap-2" style={{ alignItems:'center' }}>
          <span className="dot"/><span className="dot"/><span className="dot"/>
          <span className="micro" style={{ marginLeft: 6, color:'var(--fg-3)' }}>ai.xapi.to</span>
        </div>
        {/* 工具 tab + 复制 —— 切 tab 同时切接入方式 / 路由示例 */}
        <div className="row gap-2" style={{ alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
          {Object.entries(HERO_SNIPPETS).map(([key, s]) => {
            const active = proto === key;
            return (
              <button
                key={key}
                onClick={() => setProto(key)}
                className="micro-mono"
                style={{
                  padding:'3px 9px', borderRadius: 4, cursor:'pointer',
                  fontSize: 10.5, letterSpacing:'0.04em', textTransform:'none',
                  background: active ? 'rgba(95,208,168,0.12)' : 'transparent',
                  color: active ? 'var(--c-mint)' : 'var(--fg-4)',
                  border: `1px solid ${active ? 'rgba(95,208,168,0.40)' : 'var(--line)'}`,
                  transition:'background .15s, color .15s, border-color .15s',
                }}
              >
                {s.label}
              </button>
            );
          })}
          <button
            onClick={copy}
            title={t(copied ? 'ai.hero.term.copied' : 'ai.hero.term.copy')}
            aria-label={t('ai.hero.term.copy')}
            style={{
              display:'inline-flex', alignItems:'center', cursor:'pointer',
              padding: 4, borderRadius: 4, background:'transparent',
              border:'1px solid var(--line)', color: copied ? 'var(--ok)' : 'var(--fg-4)',
              transition:'color .15s',
            }}
          >
            {copied ? <XIcons.IconCheck size={12}/> : <XIcons.IconCopy size={12}/>}
          </button>
        </div>
      </div>

      <div className="term-panel-body">
        {/* CLI 接入示例 —— base_url / BASE_URL 行高亮，强调「改一行就接管」 */}
        <div>
          {codeText.split('\n').map((line, i) => (
            <HeroCodeLine key={i} line={line} highlight={snip.highlight} note={t('ai.hero.term.baseUrlNote')} strategySuffix={strategySuffix} />
          ))}
        </div>

        {/* 路由决策 —— 自动选最便宜的健康上游 */}
        <div style={{ marginTop: 14 }}>
          <span className="term-comment">{'// '}{t('ai.hero.term.routeLabel')}</span>
        </div>
        <div className="row gap-2" style={{ alignItems:'center', marginTop: 2 }}>
          <span style={{ color:'var(--c-mint)' }}>{'>'}</span>
          <span style={{ color:'var(--fg-1)' }}>
            {snip.route.model} → {snip.route.upstream}
            <span style={{ color:'var(--fg-4)' }}>{t('ai.hero.term.cheapestHealthy')}</span>
          </span>
          <span className="row gap-1" style={{ alignItems:'center' }}>
            <span className="live-dot" style={{ width: 6, height: 6 }}/>
            <span className="micro-mono" style={{ color:'var(--c-mint)', textTransform:'none', letterSpacing: 0 }}>
              {t('ai.hero.term.healthy')}
            </span>
          </span>
        </div>

        {/* 指标 + 省钱锚点 —— 真实 USD 消耗划线对比 OpenRouter 官方价 */}
        <div className="row gap-4" style={{ marginTop: 12, flexWrap:'wrap', alignItems:'center' }}>
          {[
            { k: t('ai.hero.term.metric1.k'), v: snip.metrics.ttft },
            { k: t('ai.hero.term.metric2.k'), v: snip.metrics.tokens },
            { k: t('ai.hero.term.metric3.k'), v: snip.metrics.cost },
          ].map((m) => (
            <span key={m.k}>
              <span style={{ color:'var(--fg-4)' }}>{m.k} </span>
              <span className="mint-text">{m.v}</span>
            </span>
          ))}
          <span className="row gap-1" style={{ alignItems:'center' }}>
            <s className="mono" style={{ fontSize: 11, color:'var(--fg-4)' }}>{snip.metrics.official}</s>
            <span className="mono" style={{
              fontSize: 10, fontWeight: 600, color:'var(--c-mint)',
              padding:'0 4px', borderRadius: 3,
              background:'rgba(95,208,168,0.12)', border:'1px solid rgba(95,208,168,0.28)',
            }}>
              {t('ai.hero.term.save').replace('{P}', String(snip.metrics.save))}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------- page ----------

// AI 类目过滤集 —— 前后端有过 'AI Models' / 'AI-Models' 两种写法，全部纳入
const AI_CATEGORIES = new Set([
  'AI Models', 'AI-Models',
  'AI Services', 'AI-Services',
]);

// 暂不展示的上游 —— 在定价过滤 chip 和上游池卡片中都隐藏
// 取消注释下方任一行可重新放开。
const HIDDEN_UPSTREAMS = new Set([
  'OpenRouter',           // marketplace service.name
  'OpenRouter API',       // 有的 marketplace 行用了 " API" 后缀
]);
const HIDDEN_HOSTS = new Set(['openrouter']);   // marketplace 的 host slug

// 格式化美金价格 —— 小于 0.1 保留 3 位小数避免四舍五入到 $0.0
const fmtPrice = formatPrice;

// OpenRouter 官方价不再前端硬编码 —— 由后端 /models/catalog 实时给出
// (canonical.openrouterInput/OutputPrice，OpenRouter sync 维护)，避免手工快照漂移。

// 优惠门槛：相对官方价低于该比例视为「与官方持平」，不展示优惠
const DISCOUNT_MIN = 0.01;

// 折扣百分比：(官方价 − 我们价) / 官方价。仅当我们更便宜（≥1%）时返回整数百分比，
// 否则返回 null —— 持平或我们更贵都不展示优惠（符合「价格相同则不显示优惠」）。
const discountPct = (ours, official) => {
  if (ours == null || !(official > 0) || !(ours >= 0)) return null;
  const d = (official - ours) / official;
  return d >= DISCOUNT_MIN ? Math.round(d * 100) : null;
};

// 单列价格单元：我们的价（高亮）+（仅当更便宜时）官方价划线 + -xx% 优惠徽标。
// input / output 各自独立计算优惠，因此两列分别决定是否展示徽标。
const PriceCell = ({ ours, official, width = 150, t, rates, direction }) => {
  if (rates) return <span style={{ textAlign: 'right', fontSize: 12 }}>{audioPriceText(rates, direction)}<small style={{ display: 'block' }}>OpenRouter: {audioPriceText(rates, direction, true)}</small></span>;
  const pct = discountPct(ours, official);
  return (
    <span style={{
      width, display:'inline-flex', flexDirection:'column',
      alignItems:'flex-end', gap: 3,
    }}>
      <span className="mono mint-text" style={{ fontSize: 12, fontWeight: 500 }}>
        {fmtPrice(ours)}{ours == null && official != null && <small> OpenRouter: {fmtPrice(official)}</small>}
      </span>
      {pct != null && (
        <span className="row gap-1" style={{ alignItems:'center', justifyContent:'flex-end' }}>
          <s className="mono" title={t('ai.catalog.discountHint')}
            style={{ fontSize: 10.5, color:'var(--fg-4)' }}>
            {fmtPrice(official)}
          </s>
          <span className="mono" style={{
            fontSize: 10, fontWeight: 600, color:'var(--c-mint)',
            padding:'0 4px', borderRadius: 3,
            background:'rgba(95,208,168,0.12)', border:'1px solid rgba(95,208,168,0.28)',
          }}>
            -{pct}%
          </span>
        </span>
      )}
    </span>
  );
};

// 表头行 —— 多视图共用的样式
const TableHeader = ({ children }) => (
  <div className="row" style={{
    padding:'12px 18px', borderBottom:'1px solid var(--line)',
    fontFamily:'var(--font-mono)', fontSize: 10.5, letterSpacing:'0.14em',
    textTransform:'uppercase', color:'var(--fg-4)', background:'var(--bg-2)',
  }}>{children}</div>
);

// 去重视图 (filter=All)：每行 = 一个 model，展示「单上游 in+out 之和最低」的组合价。
// 副标题标出该组合价来自哪个上游；input / output 两列各自对比 OpenRouter 官方价。
// 上游明细 hover 内容（base-ui Tooltip）：每个上游 name + 单上游 in/out（USD/1M），
// 后端已按总价升序排好，首个标 ★（即最低价来源）。
const UpstreamPopup = ({ rows, t }) => {
  if (!rows || !rows.length) {
    return <span style={{ color:'var(--fg-4)' }}>{t('ai.catalog.noUpstreams')}</span>;
  }
  return (
    <div style={{ minWidth: 220 }}>
      <div className="micro-mono" style={{
        color:'var(--fg-4)', letterSpacing:'0.08em', textTransform:'uppercase',
        fontSize: 9.5, marginBottom: 7,
      }}>{t('ai.catalog.upstreamsTitle')}</div>
      <div style={{ display:'flex', flexDirection:'column', gap: 5 }}>
        {rows.map((u, i) => (
          <div key={i} className="row" style={{ justifyContent:'space-between', gap: 16, alignItems:'baseline' }}>
            <span style={{
              color: i === 0 ? 'var(--c-mint)' : 'var(--fg-2)', fontSize: 11.5,
              minWidth: 0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
            }}>
              {i === 0 && !u.audioRates ? '★ ' : ''}{u.name}
            </span>
            <span className="mono" style={{ color:'var(--fg-3)', fontSize: 11, whiteSpace:'nowrap', flexShrink: 0 }}>
              {u.audioRates ? <>{audioPriceText(u.audioRates, 'input')} / {audioPriceText(u.audioRates, 'output')}</> : <>{fmtPrice(u.in)} / {fmtPrice(u.out)} · USD / 1M tokens{u.in == null && u.referencePrices && <small> OpenRouter: {fmtPrice(u.referencePrices.inputPer1M)} / {fmtPrice(u.referencePrices.outputPer1M)}</small>}</>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// 调用量单元：调用次数(凸显·大号 mint) + token 消耗子行 + 相对最大值的占比条。
const CallsCell = ({ calls, tokens, max, t, width = 130 }) => {
  const pct = max > 0 && calls > 0 ? Math.max(3, Math.round((calls / max) * 100)) : 0;
  return (
    <span style={{ width, display:'inline-flex', flexDirection:'column', alignItems:'flex-end', gap: 3 }}>
      <span className="mono mint-text" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1 }}>
        {XPrim.fmt(calls || 0)}
      </span>
      {tokens > 0 && (
        <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10 }}>
          {t('ai.catalog.tokensVia').replace('{N}', XPrim.fmt(tokens))}
        </span>
      )}
      <span style={{ width: 64, height: 3, borderRadius: 2, background:'var(--bg-2)', overflow:'hidden', marginTop: 1 }}>
        <span style={{ display:'block', height:'100%', width: pct + '%', background:'var(--c-mint)', opacity: 0.55 }} />
      </span>
    </span>
  );
};

// 横排紧凑价格：我们的价(高亮) +（仅更便宜时）官方划线价 + -xx% —— 卡片/排行视图用。
const PriceInline = ({ ours, official, t, rates, direction }) => {
  if (rates) return <span style={{ fontSize: 12 }}>{audioPriceText(rates, direction)}<small style={{ display: 'block' }}>OpenRouter: {audioPriceText(rates, direction, true)}</small></span>;
  const pct = discountPct(ours, official);
  return (
    <span className="row gap-1" style={{ alignItems:'baseline', justifyContent:'flex-end' }}>
      <span className="mono mint-text" style={{ fontSize: 13, fontWeight: 500 }}>{fmtPrice(ours)}{ours == null && official != null && <small> OpenRouter: {fmtPrice(official)}</small>}</span>
      {pct != null && (
        <>
          <s className="mono" style={{ fontSize: 10, color:'var(--fg-4)' }}>{fmtPrice(official)}</s>
          <span className="mono" style={{
            fontSize: 9.5, fontWeight: 600, color:'var(--c-mint)', padding:'0 3px',
            borderRadius: 3, background:'rgba(95,208,168,0.12)', border:'1px solid rgba(95,208,168,0.28)',
          }}>-{pct}%</span>
        </>
      )}
    </span>
  );
};

// 「N 个上游」徽标 + hover 明细（base-ui Tooltip）—— 三种视图共用。
const UpstreamBadge = ({ r, t, side = 'top' }) => (
  <Tooltip content={<UpstreamPopup rows={r.upstreams} t={t} />} side={side} align="center">
    <span style={{
      display:'inline-flex', alignItems:'center', gap: 4,
      fontSize: 11, padding:'2px 8px', borderRadius: 3, cursor:'help',
      color: r.providerCount > 1 ? 'var(--c-mint)' : 'var(--fg-3)',
      background: r.providerCount > 1 ? 'rgba(95,208,168,0.10)' : 'var(--bg-2)',
      border: `1px solid ${r.providerCount > 1 ? 'rgba(95,208,168,0.30)' : 'var(--line-2)'}`,
      letterSpacing:'0.04em',
    }}>
      {t('ai.catalog.upstreamsN').replace('{N}', r.providerCount)}
    </span>
  </Tooltip>
);

const CategoryBadge = ({ r, t }) => (
  <span className="micro-mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginLeft: 8 }}>
    {r.category ? t(`ai.catalog.category.${r.category}`) : ''}
    {r.pricingMode === 'actual_cost' && ` · ${t('ai.catalog.actualCost')}`}
    {r.pricingMode === 'unknown' && ` · ${t('ai.catalog.dynamicCost')}`}
  </span>
);

// ===== 视图 B：紧凑表格 —— 收窄居中、模型/厂商/来自上游 左侧成组、数字列拉到一起 =====
const TABLE_GRID = SHOW_CALL_VOLUME
  ? 'minmax(0,1fr) 140px 150px 150px 100px'
  : 'minmax(0,1fr) 150px 150px 100px';
const PricingTable = ({ rows, t }) => {
  const maxCalls = Math.max(1, ...rows.map((r) => r.calls || 0));
  return (
    <TooltipProvider>
      <div className="card" style={{ padding: 0, overflow:'hidden' }}>
        <div style={{
          display:'grid', gridTemplateColumns: TABLE_GRID, gap: 12, padding:'12px 20px',
          borderBottom:'1px solid var(--line)', background:'var(--bg-2)',
          fontFamily:'var(--font-mono)', fontSize: 10.5, letterSpacing:'0.12em',
          textTransform:'uppercase', color:'var(--fg-4)',
        }}>
          <span>{t('ai.catalog.colModel')}</span>
          {SHOW_CALL_VOLUME && <span style={{ textAlign:'right' }}>{t('ai.catalog.colCalls')}</span>}
          <span style={{ textAlign:'right' }}>{t('ai.catalog.colIn')}</span>
          <span style={{ textAlign:'right' }}>{t('ai.catalog.colOut')}</span>
          <span style={{ textAlign:'right' }}>{t('ai.catalog.colProviders')}</span>
        </div>
        {rows.map((r, i) => (
          <div key={r.id} className="ai-catalog-row" style={{
            display:'grid', gridTemplateColumns: TABLE_GRID, gap: 12, alignItems:'center',
            padding:'13px 20px', borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--line)',
          }}>
            <div style={{ minWidth: 0 }}>
              <code className="mono" style={{ fontSize: 13, fontWeight: 500, color:'var(--fg-0)' }}>{r.id}</code><CategoryBadge r={r} t={t} />{!r.audioRates && <small style={{ marginLeft: 6 }}>USD / 1M tokens</small>}
              <div className="micro-mono" style={{
                marginTop: 3, color:'var(--fg-4)', fontSize: 10,
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
              }}>
                {r.family}{r.bestUpstream ? ` · ${t('ai.catalog.via')} ${r.bestUpstream}` : ''}
              </div>
            </div>
            {SHOW_CALL_VOLUME && <span style={{ textAlign:'right' }}><CallsCell calls={r.calls} tokens={r.tokens} max={maxCalls} t={t} width="100%" /></span>}
            <PriceCell rates={r.audioRates} direction="input" ours={r.bestIn} official={r.official?.in} t={t} width="100%" />
            <PriceCell rates={r.audioRates} direction="output" ours={r.bestOut} official={r.official?.out} t={t} width="100%" />
            <span style={{ textAlign:'right' }}><UpstreamBadge r={r} t={t} side="left" /></span>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
};

// ===== 视图 A：卡片网格 —— 每模型一张卡，留白舒服 =====
const PricingCards = ({ rows, t }) => {
  const maxCalls = Math.max(1, ...rows.map((r) => r.calls || 0));
  return (
    <TooltipProvider>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {rows.map((r) => {
          const pct = r.calls > 0 ? Math.max(3, Math.round((r.calls / maxCalls) * 100)) : 0;
          return (
            <div key={r.id} className="card ai-catalog-row" style={{ padding: 16, display:'flex', flexDirection:'column', gap: 12 }}>
              <div className="row" style={{ justifyContent:'space-between', alignItems:'flex-start', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <code className="mono" style={{ fontSize: 14, fontWeight: 600, color:'var(--fg-0)' }}>{r.id}</code><CategoryBadge r={r} t={t} />{!r.audioRates && <small style={{ marginLeft: 6 }}>USD / 1M tokens</small>}
                  {r.bestUpstream && (
                    <div className="micro-mono" style={{
                      marginTop: 3, color:'var(--fg-4)', fontSize: 10,
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                    }}>{t('ai.catalog.via')} {r.bestUpstream}</div>
                  )}
                </div>
                <span className="micro-mono" style={{
                  flexShrink: 0, fontSize: 10, padding:'2px 7px', borderRadius: 3,
                  color:'var(--fg-3)', background:'var(--bg-2)', border:'1px solid var(--line-2)',
                  letterSpacing:'0.06em', textTransform:'uppercase',
                }}>{r.family}</span>
              </div>
              {SHOW_CALL_VOLUME && (
                <div>
                  <div className="row" style={{ alignItems:'baseline', gap: 6 }}>
                    <span className="mono mint-text" style={{ fontSize: 18, fontWeight: 600 }}>{XPrim.fmt(r.calls || 0)}</span>
                    <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10 }}>{t('ai.catalog.colCalls')}</span>
                    {r.tokens > 0 && (
                      <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10, marginLeft:'auto' }}>
                        {t('ai.catalog.tokensVia').replace('{N}', XPrim.fmt(r.tokens))}
                      </span>
                    )}
                  </div>
                  <span style={{ display:'block', width:'100%', height: 3, borderRadius: 2, background:'var(--bg-2)', overflow:'hidden', marginTop: 6 }}>
                    <span style={{ display:'block', height:'100%', width: pct + '%', background:'var(--c-mint)', opacity: 0.55 }} />
                  </span>
                </div>
              )}
              <div style={{ display:'flex', flexDirection:'column', gap: 7, paddingTop: 11, borderTop:'1px solid var(--line)' }}>
                <div className="row" style={{ justifyContent:'space-between', alignItems:'baseline' }}>
                  <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10, textTransform:'uppercase', letterSpacing:'0.08em' }}>{t('ai.catalog.colInUpstream')}</span>
                  <PriceInline rates={r.audioRates} direction="input" ours={r.bestIn} official={r.official?.in} t={t} />
                </div>
                <div className="row" style={{ justifyContent:'space-between', alignItems:'baseline' }}>
                  <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10, textTransform:'uppercase', letterSpacing:'0.08em' }}>{t('ai.catalog.colOutUpstream')}</span>
                  <PriceInline rates={r.audioRates} direction="output" ours={r.bestOut} official={r.official?.out} t={t} />
                </div>
              </div>
              <div className="row" style={{ justifyContent:'flex-start' }}>
                <UpstreamBadge r={r} t={t} side="top" />
              </div>
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

// ===== 视图 C：排行榜列表 —— 按调用量排名的双行行卡。铺满 section，宽屏自适应多列。 =====
const PricingRankList = ({ rows, t }) => (
  <TooltipProvider>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(480px, 1fr))', gap: 12 }}>
      {rows.map((r, i) => (
        <div key={r.id} className="card ai-catalog-row" style={{
          padding:'14px 18px',
          display:'flex', flexDirection:'column', gap: 7,
        }}>
          <div className="row" style={{ justifyContent:'space-between', alignItems:'baseline', gap: 12 }}>
            <div className="row" style={{ alignItems:'baseline', gap: 10, minWidth: 0 }}>
              <span className="mono" style={{ fontSize: 12, color:'var(--fg-4)', flexShrink: 0 }}>#{i + 1}</span>
              <code className="mono" style={{ fontSize: 14, fontWeight: 600, color:'var(--fg-0)' }}>{r.id}</code><CategoryBadge r={r} t={t} />{!r.audioRates && <small style={{ marginLeft: 6 }}>USD / 1M tokens</small>}
              <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 11, flexShrink: 0 }}>{r.family}</span>
            </div>
            {SHOW_CALL_VOLUME && (
              <span className="mono mint-text" style={{ fontSize: 15, fontWeight: 600, flexShrink: 0 }}>
                {XPrim.fmt(r.calls || 0)}{' '}
                <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10, fontWeight: 400 }}>{t('ai.catalog.colCalls')}</span>
              </span>
            )}
          </div>
          <div className="row" style={{ justifyContent:'space-between', alignItems:'baseline', gap: 12, fontSize: 12 }}>
            <div className="row" style={{ alignItems:'baseline', gap: 8, flexWrap:'wrap' }}>
              <span className="row gap-1" style={{ alignItems:'baseline' }}>
                <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10 }}>{t('ai.catalog.in')}</span>
                <PriceInline rates={r.audioRates} direction="input" ours={r.bestIn} official={r.official?.in} t={t} />
              </span>
              <span style={{ color:'var(--fg-4)' }}>·</span>
              <span className="row gap-1" style={{ alignItems:'baseline' }}>
                <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10 }}>{t('ai.catalog.out')}</span>
                <PriceInline rates={r.audioRates} direction="output" ours={r.bestOut} official={r.official?.out} t={t} />
              </span>
              <span style={{ color:'var(--fg-4)' }}>·</span>
              <UpstreamBadge r={r} t={t} side="top" />
            </div>
            {SHOW_CALL_VOLUME && r.tokens > 0 && (
              <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 10, flexShrink: 0 }}>
                {t('ai.catalog.tokensVia').replace('{N}', XPrim.fmt(r.tokens))}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  </TooltipProvider>
);

// 视图切换器：cards / table / list 三选一。
export const PricingCatalog = ({ rows, view, t }) => {
  if (view === 'cards') return <PricingCards rows={rows} t={t} />;
  if (view === 'list') return <PricingRankList rows={rows} t={t} />;
  return <PricingTable rows={rows} t={t} />;
};

const ViewToggle = ({ view, setView, t }) => {
  const opts = [
    ['cards', t('ai.catalog.viewCards')],
    ['table', t('ai.catalog.viewTable')],
    ['list', t('ai.catalog.viewList')],
  ];
  return (
    <div className="row" style={{ border:'1px solid var(--line)', borderRadius: 5, overflow:'hidden', background:'var(--bg-1)' }}>
      {opts.map(([k, label], i) => {
        const active = view === k;
        return (
          <button key={k} onClick={() => setView(k)} className="micro-mono" style={{
            padding:'7px 14px', fontSize: 11, letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer',
            border:'none', borderLeft: i ? '1px solid var(--line)' : 'none',
            background: active ? 'rgba(95,208,168,0.12)' : 'transparent',
            color: active ? 'var(--c-mint)' : 'var(--fg-3)',
          }}>{label}</button>
        );
      })}
    </div>
  );
};

export const AIPage = () => {
  const { navigate } = XShell.useRoute();
  const { t } = useT();
  const { authed } = XShell.useAuth();
  const [loaded, setLoaded] = uS(false);
  uE(() => { const id = requestAnimationFrame(() => setLoaded(true)); return () => cancelAnimationFrame(id); }, []);

  // ============ 真实 marketplace 数据 —— AI 类目下的所有上游中转服务 ============
  // 走和 /market 一样的 listMarketplace + statsService.publicBatch；
  // 唯一区别是只 keep AI 相关 category，再按调用量排序作为"主推上游"。
  const [relays, setRelays] = uS([]);
  const [relaysLoading, setRelaysLoading] = uS(true);
  uE(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await XApi.apiSvc.listMarketplace().catch(() => []);
        if (cancelled) return;
        const aiOnly = (Array.isArray(list) ? list : []).filter(
          (a) =>
            AI_CATEGORIES.has(a.category) &&
            !HIDDEN_UPSTREAMS.has(a.name) &&
            !HIDDEN_HOSTS.has(a.host),
        );
        if (!aiOnly.length) {
          setRelays([]);
          return;
        }
        const mapped = aiOnly.map((a) => ({
          id: a.id,
          slug: a.host || a.slug || a.id,
          name: a.name,
          provider: a.provider || null,
          // 用真实的 AI gateway subdomain：{host}.ai.xapi.to —— 用户可以直连
          // 这个上游用 OpenAI Chat Completions 等非 Anthropic 协议
          host: a.host ? `${a.host}.ai.xapi.to` : '',
          category: a.category || 'AI Models',
          description: a.description || '',
          logoUrl: a.logoUrl || '',
          hits: a.calls30d || 0,
          uptime: a.uptime != null ? Number(a.uptime) : 100,
          latency: a.latency != null ? Number(a.latency) : 0,
          verified: !!a.isPublic,
          spark: null,
        }));

        // 富化 stats 和 sparkline —— best-effort
        try {
          const ids = mapped.map((m) => m.id).filter(Boolean);
          const [statsMap, tsMap] = await Promise.all([
            XApi.statsService.publicBatch(ids).catch(() => ({})),
            XApi.statsService.timeseriesBatch
              ? XApi.statsService.timeseriesBatch(ids, 30).catch(() => ({}))
              : Promise.resolve({}),
          ]);
          mapped.forEach((m) => {
            const s = statsMap && statsMap[m.id];
            if (s) {
              if (s.uptime != null) m.uptime = Number(s.uptime);
              if (s.avgLatency != null) m.latency = Math.round(Number(s.avgLatency));
              if (s.totalHits != null) m.hits = Number(s.totalHits);
            }
            const ts = tsMap && tsMap[m.id];
            if (ts && Array.isArray(ts.points) && ts.points.length) {
              const series = ts.points.map((p) => p.hits || 0);
              if (series.some((v) => v > 0)) m.spark = series;
            }
          });
        } catch {}

        // 调用量降序 —— 旗舰上游放前面
        mapped.sort((a, b) => (b.hits || 0) - (a.hits || 0));
        if (!cancelled) setRelays(mapped);
      } finally {
        if (!cancelled) setRelaysLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Real per-upstream model pricing — fully API-driven (GET /api/models).
  // 后端按 canonical id 聚合（apps/api ModelsService），返回每个 model 的所有
  // providers + pricing。这里展平成 per-(upstream, model) 行供下方表格用。
  // 等价于早期 hardcoded XData.AI_MODEL_PRICING，但数据**实时来自 DB**，
  // admin 在 /register 改了价格立刻反映在这里。
  // /ai 价格表数据源 = GET /models/catalog（canonical-only + 按调用量排 +
  // OpenRouter 折扣 + admin displayWeight/hidden 已在后端处理）。
  const [catalog, setCatalog] = uS([]);
  uE(() => {
    let cancelled = false;
    loadModelCatalog(params => XApi.modelsService.catalog(params), () => cancelled)
      .then((res) => { if (!cancelled) setCatalog(Array.isArray(res) ? res : []); })
      .catch(() => { if (!cancelled) setCatalog([]); });
    return () => { cancelled = true; };
  }, []);

  // vendor 字段后端是全小写（"anthropic"），UI 上想看 "Anthropic"。
  const vendorDisplay = (v) => {
    if (!v) return '其他';
    const map = {
      openai: 'OpenAI', anthropic: 'Anthropic', google: 'Google',
      deepseek: 'DeepSeek', xai: 'xAI', alibaba: '阿里 Qwen', moonshot: 'Moonshot',
      zhipu: '智谱 GLM', mistral: 'Mistral', minimax: 'MiniMax', bytedance: '字节 Doubao',
      xiaomi: '小米 MiMo', kuaishou: '快手 Kling', tencent: '腾讯 Hunyuan',
      lumalabs: 'Luma', runwayml: 'Runway', pikalabs: 'Pika',
    };
    return map[v] || v.charAt(0).toUpperCase() + v.slice(1);
  };

  // 后端 /api/models 直接返每个 canonical 下的 providers 列表。前端只做：
  //   1. 规范化 id 展示（lowercase + . → -，兜底 unmapped 也变规范形）
  //   2. 算 min input / output（跨该 canonical 的所有 provider）
  //   3. 收集 upstream 列表给 hover tooltip 用
  //   4. 一行 = 一个 model，不再 per-(upstream, model) 展平
  // catalog 项 → 表格行。后端已给最低价(USD/1M)、OpenRouter 参考价、vendor、
  // 调用量、provider 数；前端只做展示映射(official = OpenRouter 价,供 PriceCell 算折扣)。
  const modelRows = uM(() => {
    return (catalog || []).map(it => ({ ...catalogRow(it), family: vendorDisplay(it.vendor), mapped: true }));
  }, [catalog]);

  // 厂商过滤 pills（按 vendor，不再按上游服务）。
  const vendors = uM(() => {
    const seen = new Set();
    const list = [];
    for (const r of modelRows) {
      const v = r.vendor || '其他';
      if (!seen.has(v)) { seen.add(v); list.push(v); }
    }
    return ['All', ...list];
  }, [modelRows]);
  const [activeVendor, setActiveVendor] = uS('All');
  const [activeCategory, setActiveCategory] = uS('All');
  const [modelQuery, setModelQuery] = uS('');
  const [viewMode, setViewMode] = uS('cards'); // cards | table | list
  const [vendorsExpanded, setVendorsExpanded] = uS(false);
  const CATALOG_TOP_N = 24;
  const VENDOR_COLLAPSED_COUNT = 5; // 收起态展示的厂商 chip 数（含「全部」，即「全部」+ 4 个厂商）

  // 收起态只显前 N 个；但当前选中项必须可见，否则用户看不到自己选了谁。
  const visibleVendors = uM(() => {
    if (vendorsExpanded || vendors.length <= VENDOR_COLLAPSED_COUNT) return vendors;
    const head = vendors.slice(0, VENDOR_COLLAPSED_COUNT);
    if (activeVendor !== 'All' && !head.includes(activeVendor)) head.push(activeVendor);
    return head;
  }, [vendors, vendorsExpanded, activeVendor]);
  const hiddenVendorCount = vendors.length - VENDOR_COLLAPSED_COUNT;

  // 厂商 + 搜索过滤（后端已按 displayWeight → 调用量排好，前端不再重排）。
  const searchedRows = uM(() => {
    const q = modelQuery.trim().toLowerCase();
    return modelRows.filter((r) => {
      if (activeCategory !== 'All' && r.category !== activeCategory) return false;
      if (activeVendor !== 'All' && (r.vendor || '其他') !== activeVendor) return false;
      if (q && !(r.id.toLowerCase().includes(q) || String(r.family || '').toLowerCase().includes(q)))
        return false;
      return true;
    });
  }, [modelRows, activeVendor, activeCategory, modelQuery]);
  // /ai 仅作预览，恒定只显 top-N；"查看全部" 跳转到 /ai/models 专页。
  const visibleRows = searchedRows.slice(0, CATALOG_TOP_N);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="hero-section" style={{
        position:'relative', overflow:'hidden', background:'var(--bg-0)',
        minHeight:'calc(66vh - 52px)', display:'flex', flexDirection:'column',
      }}>
        <div className="grid-bg" style={{
          position:'absolute', inset: 0, opacity: 0.18,
          maskImage:'linear-gradient(180deg, #000 0%, transparent 100%)',
          WebkitMaskImage:'linear-gradient(180deg, #000 0%, transparent 100%)',
        }}/>
        <div className="hero-blob" style={{
          width: 540, height: 540, left:'-10%', top:'-20%', opacity: 0.35,
          background:'radial-gradient(circle, color-mix(in oklab, var(--c-mint) 22%, transparent) 0%, transparent 60%)',
        }}/>

        <div className="hero-eyebrow" style={{
          maxWidth: 1480, margin:'0 auto', padding:'56px 40px 20px',
          position:'relative', width:'100%',
        }}>
          <div className={`reveal ${loaded ? 'is-revealed' : ''}`} style={{
            display:'flex', alignItems:'center', gap: 12, color:'var(--fg-3)',
            fontFamily:'var(--font-mono)', fontSize: 12,
            letterSpacing:'0.12em', textTransform:'uppercase',
          }}>
            <span className="live-dot"/>
            <span style={{ color:'var(--c-mint)' }}>xapi</span>
            <span style={{ color:'var(--fg-4)' }}>//</span>
            <span>{t('ai.kicker')}</span>
          </div>
        </div>

        <div className="hero-grid" style={{
          maxWidth: 1480, margin:'0 auto', padding:'24px 40px 48px',
          position:'relative', display:'grid',
          gridTemplateColumns:'1.15fr 0.85fr', gap: 56, alignItems:'center',
          flex: 1, width:'100%',
        }}>
          <div>
            <h1 className={`reveal delay-1 ${loaded ? 'is-revealed' : ''}`} style={{
              margin: 0,
              fontSize:'clamp(40px, 5.2vw, 76px)',
              lineHeight: 0.98,
              letterSpacing:'-0.045em',
              fontWeight: 600,
              color:'var(--fg-0)',
              wordBreak:'keep-all',
            }}>
              <Balancer>
                {t('ai.hero.titleA')}{' '}
                <span className="slash-mark">//</span>{' '}
                <span className="mint-text">{t('ai.hero.titleB')}</span>
              </Balancer>
            </h1>

            <div className="row gap-6" style={{ marginTop: 28, flexWrap:'wrap' }}>
              <p className={`reveal delay-2 ${loaded ? 'is-revealed' : ''} hero-desc-desktop`} style={{
                margin: 0, fontSize: 16, color:'var(--fg-2)', maxWidth: 560, lineHeight: 1.6,
              }}>
                <Balancer>{t('ai.hero.description')}</Balancer>
              </p>
              <p className={`reveal delay-2 ${loaded ? 'is-revealed' : ''} hero-desc-mobile`} style={{
                margin: 0, fontSize: 15, color:'var(--fg-2)', maxWidth: 520, lineHeight: 1.55,
              }}>
                {t('ai.hero.descriptionMobile')}
              </p>
            </div>

            <div className={`reveal delay-3 ${loaded ? 'is-revealed' : ''} row gap-3`} style={{ marginTop: 28, flexWrap: 'wrap' }}>
              <button className="btn btn-primary btn-lg" onClick={() => navigate(authed ? 'console' : 'login')}>
                <span className="arrow">→</span> {t('ai.hero.cta.primary')}
              </button>
              <button className="btn btn-lg" onClick={() => window.open(DOCS_URL, '_blank', 'noopener')}>
                <XIcons.IconBook size={14}/> {t('ai.hero.cta.secondary')}
              </button>
              {/* 3rd CTA — opens the models catalog (/ai/models). Routes through
                  the modelsService backend aggregator (~10 active models today). */}
              <button className="btn btn-lg" onClick={() => navigate('models')}>
                <XIcons.IconList size={14}/> {t('ai.hero.cta.models')}
              </button>
            </div>

            {/* Base URL chip —— 强调所有 AI 上游统一接入点 */}
            <BaseUrlChip loaded={loaded} t={t}/>
          </div>

          <HeroTerminal loaded={loaded}/>
        </div>
      </section>

      {/* ============ SECTION 01 — Stats strip (real numbers from marketplace) ============ */}
      <RevealSection className="sec-pad-tight section-seam">
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div className="row" style={{
            flexWrap:'wrap', gap: 'clamp(20px, 4vw, 56px)',
            justifyContent:'space-between', alignItems:'center',
            fontFamily:'var(--font-mono)', fontSize: 13, color:'var(--fg-3)',
            letterSpacing:'0.02em',
          }}>
            <span className="row gap-2" style={{ alignItems:'center' }}>
              <span className="live-dot"/>
              <span className="mint-text">{t('ai.stats.providers').replace('{N}', relaysLoading ? '…' : String(relays.length))}</span>
            </span>
            {SHOW_CALL_VOLUME && (
              <>
                <span style={{ color:'var(--fg-4)' }}>·</span>
                <span>
                  <span className="mint-text">
                    {t('ai.stats.calls').replace('{N}', relaysLoading ? '…' : XPrim.fmt(relays.reduce((s, r) => s + (r.hits || 0), 0)))}
                  </span>
                </span>
              </>
            )}
            <span style={{ color:'var(--fg-4)' }}>·</span>
            <span>{t('ai.stats.models').replace('{N}', String(modelRows.length))}</span>
            <span style={{ color:'var(--fg-4)' }}>·</span>
            <span><span className="mint-text">{t('ai.stats.compat')}</span></span>
          </div>
        </div>
      </RevealSection>

      {/* ============ SECTION 02 — Real per-upstream pricing ============
          数据来自生产 api_endpoints.tokenPricing。每行 = 上游 × 模型 × 真实
          USD/1M token 价格。提供上游过滤器，方便用户对比不同上游同款模型的差价。 */}
      <RevealSection className="sec-pad-regular section-seam" style={{ background:'var(--bg-0)' }}>
        <div id="ai-catalog" className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div style={{ marginBottom: 28 }}>
            <div className="sec-num"><span className="grey">01</span> &nbsp;{t('ai.catalog.eyebrow')}</div>
            <h2 className="gs-section-title" style={{
              margin:'14px 0 10px', fontSize:'clamp(30px, 4vw, 46px)',
              letterSpacing:'-0.035em', fontWeight: 600, lineHeight: 1.05, textWrap:'balance',
            }}>
              {t('ai.catalog.titleA')} <span className="slash-mark">//</span>{' '}
              <span style={{ color:'var(--fg-3)' }}>{t('ai.catalog.titleB')}</span>
            </h2>
            <p style={{ margin: 0, color:'var(--fg-3)', fontSize: 14, lineHeight: 1.65, maxWidth: 760 }}>
              {t('ai.catalog.summary')}
            </p>
          </div>

          {/* 模型搜索框 + 视图切换 */}
          <div className="row" style={{ marginBottom: 14, justifyContent:'space-between', gap: 12, flexWrap:'wrap' }}>
            <input
              value={modelQuery}
              onChange={(e) => { setModelQuery(e.target.value); }}
              placeholder={t('ai.catalog.search')}
              className="micro-mono"
              style={{
                flex:'1 1 240px', minWidth: 200, maxWidth: 380,
                padding:'8px 12px', borderRadius: 4, fontSize: 12,
                background:'var(--bg-1)', color:'var(--fg-1)',
                border:'1px solid var(--line)', outline:'none',
              }}
            />
            <ViewToggle view={viewMode} setView={setViewMode} t={t} />
          </div>

          {/* 厂商过滤 chips（按 vendor 展示，默认收起一行，可展开/收起） */}
          <div className="row gap-2" style={{ flexWrap:'wrap', marginBottom: 18 }}>
            {visibleVendors.map((u) => {
              const active = activeVendor === u;
              const label = u === 'All' ? t('ai.catalog.filterAll') : vendorDisplay(u);
              return (
                <button
                  key={u}
                  onClick={() => setActiveVendor(u)}
                  className="micro-mono"
                  style={{
                    padding:'6px 12px', borderRadius: 4, cursor:'pointer',
                    fontSize: 11, letterSpacing:'0.08em', textTransform:'uppercase',
                    background: active ? 'rgba(95,208,168,0.10)' : 'var(--bg-1)',
                    color: active ? 'var(--c-mint)' : 'var(--fg-3)',
                    border: `1px solid ${active ? 'rgba(95,208,168,0.40)' : 'var(--line)'}`,
                    transition:'background .15s, color .15s, border-color .15s',
                  }}
                >
                  {label}
                </button>
              );
            })}
            {hiddenVendorCount > 0 && (
              <button
                onClick={() => setVendorsExpanded((v) => !v)}
                className="micro-mono"
                style={{
                  padding:'6px 12px', borderRadius: 4, cursor:'pointer',
                  fontSize: 11, letterSpacing:'0.08em', textTransform:'uppercase',
                  background:'var(--bg-1)', color:'var(--c-mint)',
                  border:'1px solid rgba(95,208,168,0.40)',
                  display:'inline-flex', alignItems:'center', gap: 5,
                }}
              >
                <span style={{ fontSize: 9, lineHeight: 1 }}>{vendorsExpanded ? '▴' : '▾'}</span>
                {vendorsExpanded
                  ? t('ai.catalog.vendorLess')
                  : t('ai.catalog.vendorMore').replace('{N}', String(hiddenVendorCount))}
              </button>
            )}
          </div>

          {/* Pricing table — dedup by model：展示「单上游 in+out 之和最低」的组合价，
              并对比 OpenRouter 官方价（input / output 各自标注 -xx% 优惠）。
              选中具体上游时 dedupedAll 已过滤为该上游的价格。 */}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }} aria-label={t('ai.catalog.categoryFilter')}>
            {['All', ...MODEL_CATEGORIES].map(category => (
              <button key={category} className={`btn btn-sm ${activeCategory === category ? 'btn-mint' : 'btn-ghost'}`} onClick={() => setActiveCategory(category)} aria-pressed={activeCategory === category}>
                {category === 'All' ? t('ai.catalog.filterAll') : t(`ai.catalog.category.${category}`)}
              </button>
            ))}
          </div>

          <PricingCatalog rows={visibleRows} view={viewMode} t={t} />

          {/* 计数 + 展开/收起（无搜索词且超过 top-N 时才显示展开） */}
          <div className="row" style={{ justifyContent:'center', alignItems:'center', gap: 16, marginTop: 18 }}>
            {visibleRows.length === 0 ? (
              <span style={{ color:'var(--fg-4)', fontSize: 13 }}>{t('ai.catalog.noMatch')}</span>
            ) : (
              <>
                <span className="micro-mono" style={{ color:'var(--fg-4)', fontSize: 11, letterSpacing:'0.06em' }}>
                  {t('ai.catalog.shownCount').replace('{X}', visibleRows.length).replace('{Y}', searchedRows.length)}
                </span>
                {searchedRows.length > CATALOG_TOP_N && (
                  <button
                    onClick={() => { navigate('models'); }}
                    className="micro-mono"
                    style={{
                      padding:'6px 16px', borderRadius: 4, cursor:'pointer',
                      fontSize: 11, letterSpacing:'0.08em', textTransform:'uppercase',
                      background:'var(--bg-1)', color:'var(--c-mint)',
                      border:'1px solid rgba(95,208,168,0.40)',
                    }}
                  >
                    {t('ai.catalog.showAll')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </RevealSection>

      {/* ============ SECTION 04 — Upstream pool (LIVE data) ============
          Cards mirror /market 的风格：彩色 category-tinted icon、verified
          checkmark、调用量 / 平均延迟 / 成功率、迷你 sparkline。
          点击卡片直接跳转到该 API 的 detail 页。 */}
      <RevealSection className="sec-pad-regular section-seam" style={{ background:'var(--bg-0)' }}>
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div style={{ marginBottom: 32 }}>
            <div className="sec-num"><span className="grey">02</span> &nbsp;{t('ai.upstream.eyebrow')}</div>
            <h2 className="gs-section-title" style={{
              margin:'14px 0 12px', fontSize:'clamp(30px, 4vw, 46px)',
              letterSpacing:'-0.035em', fontWeight: 600, lineHeight: 1.05, textWrap:'balance',
            }}>
              {t('ai.upstream.titleA').replace('{N}', String(relays.length || '—'))}{' '}
              <span className="slash-mark">//</span>{' '}
              <span style={{ color:'var(--fg-3)' }}>{t('ai.upstream.titleB')}</span>
            </h2>
            <p style={{ margin: 0, color:'var(--fg-3)', fontSize: 14, lineHeight: 1.65, maxWidth: 760 }}>
              {t('ai.upstream.summary')}
            </p>
          </div>

          {relaysLoading ? (
            <div className="ai-relay-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 16 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="card" style={{
                  padding: 22, height: 200,
                  background: 'linear-gradient(110deg, var(--bg-1) 0%, var(--bg-2) 50%, var(--bg-1) 100%)',
                  backgroundSize: '200% 100%', animation: 'shimmer 1.4s linear infinite',
                  border:'1px solid var(--line)',
                }} />
              ))}
            </div>
          ) : relays.length === 0 ? (
            <div className="card" style={{ padding: 48, textAlign:'center', border:'1px dashed var(--line)', background:'transparent' }}>
              <div className="micro" style={{ color:'var(--fg-4)', marginBottom: 10 }}>{t('ai.upstream.empty.title')}</div>
              <p style={{ color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, maxWidth: 480, margin:'0 auto' }}>
                {t('ai.upstream.empty.body')}
              </p>
            </div>
          ) : (
            <div className="ai-relay-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 16 }}>
              {relays.map((r) => <UpstreamCard key={r.id} r={r} navigate={navigate} t={t}/>)}
            </div>
          )}
        </div>
      </RevealSection>

      {/* ============ SECTION 03 — Multi-protocol native (OpenAI + Claude side-by-side) ============ */}
      <RevealSection className="sec-pad-regular section-seam">
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div className="ai-compat-grid" style={{
            display:'grid', gridTemplateColumns:'1fr 1fr', gap: 56, alignItems:'center',
          }}>
            <div>
              <div className="sec-num"><span className="grey">03</span> &nbsp;{t('ai.compat.eyebrow')}</div>
              <h2 className="gs-section-title" style={{
                margin:'14px 0 14px', fontSize:'clamp(28px, 3.6vw, 40px)',
                letterSpacing:'-0.035em', fontWeight: 600, lineHeight: 1.08, textWrap:'balance',
              }}>
                {t('ai.compat.titleA')} <span className="slash-mark">//</span>{' '}
                <span style={{ color:'var(--fg-3)' }}>{t('ai.compat.titleB')}</span>
              </h2>
              <p style={{ margin:'0 0 22px', color:'var(--fg-3)', fontSize: 14, lineHeight: 1.7, maxWidth: 520 }}>
                {t('ai.compat.body')}
              </p>
              <ul style={{ listStyle:'none', padding: 0, margin: 0, display:'flex', flexDirection:'column', gap: 10 }}>
                {['ai.compat.bullet1','ai.compat.bullet2','ai.compat.bullet3'].map((k) => (
                  <li key={k} className="row gap-2" style={{ alignItems:'flex-start', color:'var(--fg-2)', fontSize: 14 }}>
                    <span style={{ color:'var(--c-mint)', fontFamily:'var(--font-mono)' }}>→</span>
                    <span>{t(k)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="term-panel">
              <div className="term-panel-head row between">
                <div className="row gap-2" style={{ alignItems:'center' }}>
                  <span className="dot"/><span className="dot"/><span className="dot"/>
                  <span className="micro" style={{ marginLeft: 6, color:'var(--fg-3)' }}>{t('ai.compat.codeTitle')}</span>
                </div>
                <span className="micro-mono" style={{ color:'var(--fg-4)', textTransform:'none', letterSpacing: 0 }}>multi_protocol.py</span>
              </div>
              <div className="term-panel-body" style={{ paddingTop: 14, paddingBottom: 18 }}>
                {/* Anthropic SDK against the aggregated router (the featured flow) */}
                <div><span className="term-comment">{'# '}── Anthropic SDK → ai.xapi.to (aggregated, auto-route) ──</span></div>
                <div><span className="term-keyword">from</span> anthropic <span className="term-keyword">import</span> Anthropic</div>
                <div style={{ marginTop: 6 }}>ant = Anthropic(base_url=<span className="term-string">"https://ai.xapi.to"</span>, api_key=<span className="term-string">"sk-xapi-…"</span>)</div>
                <div>ant.messages.create(</div>
                <div style={{ paddingLeft: 16 }}>model=<span className="term-string">"claude-opus-4-7"</span>,</div>
                <div style={{ paddingLeft: 16 }}>messages=[{'{'}<span className="term-string">"role"</span>: <span className="term-string">"user"</span>, <span className="term-string">"content"</span>: <span className="term-string">"…"</span>{'}'}],</div>
                <div style={{ paddingLeft: 16 }}>max_tokens=<span className="term-string">1024</span>,</div>
                <div>)</div>
                <div style={{ marginTop: 4 }}>
                  <span className="term-comment">{'# '}strategy 默认 quality; 想优化速度/成本：base_url=".../speed" 或 ".../cost"</span>
                </div>

                {/* OpenAI SDK against a specific upstream subdomain */}
                <div style={{ marginTop: 16 }}><span className="term-comment">{'# '}── OpenAI SDK → 直连某个上游 (走 OpenAI Chat Completions) ──</span></div>
                <div><span className="term-keyword">from</span> openai <span className="term-keyword">import</span> OpenAI</div>
                <div style={{ marginTop: 6 }}>oai = OpenAI(base_url=<span className="term-string">"https://skyapi.ai.xapi.to/v1"</span>, api_key=<span className="term-string">"sk-xapi-…"</span>)</div>
                <div>oai.chat.completions.create(</div>
                <div style={{ paddingLeft: 16 }}>model=<span className="term-string">"gpt-5.4"</span>,</div>
                <div style={{ paddingLeft: 16 }}>messages=[{'{'}<span className="term-string">"role"</span>: <span className="term-string">"user"</span>, <span className="term-string">"content"</span>: <span className="term-string">"…"</span>{'}'}],</div>
                <div>)</div>

                <div style={{ marginTop: 12 }}>
                  <span className="term-comment">{'# '}same key, same billing — different protocol surfaces</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RevealSection>

      {/* ============ Final CTA strip ============ */}
      <RevealSection className="sec-pad-compact section-seam">
        <div className="reveal-stagger" style={{ maxWidth: 1480, margin:'0 auto', padding:'0 40px' }}>
          <div className="row between cta-strip" style={{ alignItems:'center', flexWrap:'wrap', gap: 24 }}>
            <div className="cta-strip-text" style={{ minWidth: 0, flex: 1 }}>
              <div className="sec-num"><span className="grey">→</span> &nbsp;{t('ai.cta.eyebrow')}</div>
              <h2 style={{
                margin:'10px 0 0', fontSize:'clamp(28px, 3.4vw, 40px)',
                letterSpacing:'-0.03em', fontWeight: 600, lineHeight: 1.1, textWrap:'balance',
              }}>
                {t('ai.cta.titleA')} <span className="slash-mark">//</span>{' '}
                <span style={{ color:'var(--fg-3)' }}>{t('ai.cta.titleB')}</span>
              </h2>
            </div>
            <div className="row gap-3 cta-strip-actions" style={{ flexShrink: 0 }}>
              <button className="btn btn-primary btn-lg" onClick={() => navigate(authed ? 'console' : 'login')}>
                <span className="arrow">→</span> {t('ai.cta.primary')}
              </button>
              <button className="btn btn-lg" onClick={() => window.open(DOCS_URL, '_blank', 'noopener')}>
                <XIcons.IconBook size={13}/> {t('ai.cta.secondary')}
              </button>
            </div>
          </div>
        </div>
      </RevealSection>

      <Footer/>
    </div>
  );
};

export default AIPage;
